import type { FC } from "react";
import { useEffect, useState, useRef } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Container,
  Card,
  Box,
  Grid,
  Typography,
  Breadcrumbs,
  Link,
  Button,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogActions,
  TableCell,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  FormControlLabel,
} from "@material-ui/core";
import { IColumn } from "src/components/dataTable/tableModel";
import RemoveElement from "src/hooks/useRemoveElement";
import {
  getWidgets,
  UpdateEnableStatus,
  deleteWidget,
} from "./ConnectionService";
import ChevronRightIcon from "src/icons/ChevronRight";
import Switch from "@material-ui/core/Switch";
import SearchIcon from "../../icons/Search";
import AlertMessage from "src/components/ui-components/AlertMessage";
import PencilAltIcon from "../../icons/PencilAlt";
import { Delete } from "@material-ui/icons";
import { Helmet } from "react-helmet-async";

const columns: IColumn[] = [
  { field: "displayName", headerName: "Display Name" },
  { field: "category", headerName: "Category" },
  { field: "pageGroupName", headerName: "Menu" },
  { field: "pageName", headerName: "Sub Menu" },
  { field: "isEnabled", headerName: "Is Enabled", dataType: "boolean" },
];

const WidgetsList: FC = () => {
  const navigate = useNavigate();
  const [widgetsList, setWidgetsList] = useState([]);
  const [instance, setInstance] = useState({
    flg: false,
    primaryKey: null,
    filterKey: "",
  });
  const [openDialog, setOpenDialog] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);
  const [selectedWidgetItem, setSelectedWidgetItem] = useState(null);
  const tableRef = useRef<HTMLTableElement>(null);

  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleEnabledChange = async (row) => {
    const userKey = JSON.parse(
      localStorage.getItem("userDetails")
    )?.configUserKey;
    row.isEnabled = !row.isEnabled;
    try {
      const res = await UpdateEnableStatus(
        row.wIdgetKey,
        row.isEnabled,
        userKey
      );
      if (res) {
        const successMsg = row.isEnabled ? "Widget enabled" : "Widget disabled";
        AlertMessage({ type: "success", msg: successMsg });
      }
      fetchWidgetsList();
    } catch (err) {
      console.error(err);
      AlertMessage({ type: "error", msg: err.title });
    }
  };

  const fetchWidgetsList = () => {
    getWidgets()
      .then((res: any) => {
        setWidgetsList(res || "");
      })
      .catch(() => { });
  };

  const onDeleteWidget = async () => {
    await deleteWidget(selectedWidgetItem.wIdgetKey)
      .then(() => {
        setOpenDialog(false);
        AlertMessage({
          type: "success",
          msg: "Widget deleted successfully",
        });
        fetchWidgetsList();
      })
      .catch((err) => {
        AlertMessage({ type: "error", msg: err.title });
      });
    setOpenDialog(false);
  };

  useEffect(() => {
    fetchWidgetsList();
  }, []);

  const onClickEdit = (data: any) => {
    navigate("/widget/InsertUpdateWidget", {
      state: { widgetKey: data.wIdgetKey },
    });
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleQueryChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const inputString = event.target.value.trim().toLowerCase();
    if (inputString === "") {
      fetchWidgetsList();
    } else {
      // const filteredWidgets = widgetsList.filter((widget) =>
      //   widget.displayName.toLowerCase().includes(inputString)
      // );

      const filtered = widgetsList.filter((widget) => {
        return Object.keys(widget).some((key) => {
          if (typeof widget[key] === 'string') {
            return widget[key].toLowerCase().includes(inputString.toLowerCase());
          }
          return false;
        });
      });
      setWidgetsList(filtered);
    }
  };

  const onClickCreateNew = () => {
    navigate("/widget/InsertUpdateWidget");
  };

  const paginatedWidgetsList = widgetsList.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <>
      <Helmet>
        <title>Widgets</title>
      </Helmet>
      <Box>
        <Container maxWidth={false}>
          <Grid alignItems="center" container justifyContent="space-between">
            <Grid item>
              <Breadcrumbs
                aria-label="breadcrumb"
                separator={<ChevronRightIcon fontSize="small" />}
              >
                <Link
                  color="textPrimary"
                  component={RouterLink}
                  to=""
                  variant="h5"
                >
                  Configurations
                </Link>
                <Typography color="textPrimary" variant="h5">
                  Widgets
                </Typography>
              </Breadcrumbs>
            </Grid>
            <Grid item>
              <Box sx={{ my: 2 }}>
                <Button
                  sx={{
                    background: "linear-gradient(to bottom, #072f40, #072f40)",
                    minWidth: 100,
                  }}
                  variant="contained"
                  onClick={onClickCreateNew}
                >
                  Create New
                </Button>
              </Box>
            </Grid>
          </Grid>
          <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
            <DialogContent>
              <DialogContentText>
                This will delete all the related items of Widget. Are you sure
                to delete?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setOpenDialog(false)}
                sx={{
                  background: "linear-gradient(to bottom, #939ca3, #939ca3)",
                  minWidth: 100,
                }}
                variant="contained"
                type="button"
              >
                Cancel
              </Button>
              <Button
                onClick={onDeleteWidget}
                sx={{
                  background: "linear-gradient(to bottom, #072f40, #072f40)",
                  minWidth: 100,
                }}
                variant="contained"
                type="button"
              >
                Delete
              </Button>
            </DialogActions>
          </Dialog>
          <Card>
            <Box sx={{ overflowY: "hidden" }}>
              <Grid item>
                <Box
                  sx={{
                    m: 2,
                    maxWidth: "100%",
                    alignItems: "center",
                    display: "flex",
                    width: 300,
                  }}
                >
                  <TextField
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                    onChange={handleQueryChange}
                    placeholder="Search Widgets"
                    variant="outlined"
                  />
                </Box>
              </Grid>
              <TableContainer style={{ maxHeight: "65vh", minHeight: "30vh" }}>
                <Table
                  sx={{ borderBottom: "2px solid #dee2e6" }}
                  ref={tableRef}
                  stickyHeader
                >
                  <TableHead>
                    <TableRow>
                      {columns.map((column, idx) => (
                        <TableCell
                          key={idx.toString()}
                          align="center"
                          style={{ minWidth: 130 }}
                        >
                          <Typography variant="subtitle2">
                            {
                              column.headerName
                            }
                          </Typography>
                        </TableCell>
                      ))}
                      <TableCell
                        align="center"
                        style={{ minWidth: 130 }}
                      >
                        <Typography variant="subtitle2">
                          Action
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedWidgetsList.map((row, idx) => (
                      <TableRow key={idx.toString()}>
                        {columns.map((column) => (
                          <TableCell
                            key={column.field}
                            align="center"
                            style={{ minWidth: 130 }}
                          >

                            {column.field === "isEnabled" ? (

                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={row[column.field]}
                                    onChange={() => handleEnabledChange(row)}
                                    color="primary"
                                  />
                                }
                                label=""

                              />
                            ) : (
                              <Typography variant="body2">
                                {row[column.field]}
                              </Typography>
                            )}
                          </TableCell>
                        ))}
                        <TableCell align="center" style={{ minWidth: 130 }}>
                          <IconButton onClick={() => onClickEdit(row)}>
                            <PencilAltIcon style={{ fontSize: 20 }} />
                          </IconButton>

                          <IconButton
                            type="button"
                            onClick={() => {
                              setSelectedWidgetItem(row);
                              setOpenDialog(true);
                            }}
                          >
                            <Delete style={{ fontSize: 20 }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

              </TableContainer>
              {widgetsList.length > 0 ? (
                <TablePagination
                  style={{ marginRight: 40 }}
                  component="div"
                  count={widgetsList.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[5, 10, 25]}
                />
              ) : (
                <Box
                  sx={{
                    height: 200,
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography variant="h6" sx={{ pl: 0 }}>
                    No data found!
                  </Typography>
                </Box>
              )}
            </Box>
            <RemoveElement
              instance={instance}
              setInstance={setInstance}
              setListArray={setWidgetsList}
              apiURL=""
            />
          </Card>
        </Container>
      </Box>
    </>
  );
};

export default WidgetsList;
